import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Collapse,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import colors from "../../Constants/colors";
import PhotoUploadButton from "./UploadPhotoButton";
import WorkspaceAdminId from "./WorkspaceAdminId";
import UserLimits from "./UserWorkspaceLimtis";
import {
  Features,
  USER_ONLY_FEATURES,
  WorkspaceTerminology,
} from "../../Enums/companyStatus";
import CertificationsSelector from "./CertificationField";
import NamespaceField from "./Namespaces";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import { Edit, Delete, Add } from "@mui/icons-material";
import WorkspaceExpertSelection from "../Users/WorkspaceExpertSelection";
import ModelAccessSelector from "../Common/ModelAccessSelector";
import { Expert } from "../../Models/expert";
import CustomizeWorkspaceModal from "./CustomizeWorkspaceModal";
import ComoAgentSelector from "../ComoAgents/ComoAgentSelector";
import { useFeatures } from "../../Query/useFeatures";
import { ComoInput } from "../Common/ComoInput";
import { ComoButton } from "../Common/ComoButton";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import {
  useWorkspace,
  useCreateWorkspace,
  useUpdateWorkspace,
  useWorkspaceTerminologies,
} from "../../Query/useWorkspace";
import { useTranslation } from "react-i18next";
import AlanCredentialSelection from "../Users/AlanCredentialSelection";
import {
  usePredefinedSections,
  usePutPredefinedSections,
  useDeletePredefinedSections,
} from "../../Query/usePredefinedSections";
import { AppendixStructure } from "../../Models/appendixStructure";
import PredefinedSectionEditModal from "./PredefinedSectionEditModal";

const WorkspaceCreator = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("workspaces");
  const location = useLocation();
  const { colors: workspaceColors } = useWorkspaceColors();
  const existingWorkspace = location.state?.workspace || null;
  const [experts, setExperts] = useState<Expert[]>([]);
  const [comoAgentIds, setComoAgentIds] = useState<number[]>([]);
  const [workspace, setWorkspace] = useState({
    id: existingWorkspace?.id || null,
    name: existingWorkspace?.name || "",
    description: existingWorkspace?.description || "",
    userEvaluationLimit: existingWorkspace?.user_evaluation_limit || 5,
    userCompanyLimit: existingWorkspace?.user_company_limit || 5,
    userStorageLimit: existingWorkspace?.user_storage_limit || 0,
    userLibraryLimit: existingWorkspace?.user_library_limit || 0,
    namespaces: Array.isArray(existingWorkspace?.namespaces)
      ? existingWorkspace.namespaces
      : existingWorkspace?.namespaces
        ? JSON.parse(existingWorkspace.namespaces)
        : [],
    workspaceAdminId: existingWorkspace?.workspace_admin?.id || null,
    model_ids: [] as number[],
    iconImage: null as File | null,
    existingIconUrl: existingWorkspace?.icon_image || null,
    features: [] as string[],
    certifications: [] as number[],
    primary_color: existingWorkspace?.primary_color,
    secondary_color: existingWorkspace?.secondary_color,
    accent_color: existingWorkspace?.accent_color,
    terminology: WorkspaceTerminology.DEFAULT,
    alan_credentials_id: existingWorkspace?.alan_credentials?.id || null,
    language: existingWorkspace?.language || 1,
    show_locked_features: existingWorkspace?.show_locked_features ?? false,
  });

  const { data: featuresData } = useFeatures();
  const features = featuresData?.features || [];

  const [expandAll, setExpandAll] = useState<boolean>(false);
  const [isChanged, setIsChanged] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { data: terminologyData } = useWorkspaceTerminologies();
  const { data: predefinedSectionsData } = usePredefinedSections(
    workspace.id ?? undefined,
  );
  const putPredefinedSections = usePutPredefinedSections(
    workspace.id ?? undefined,
  );
  const deletePredefinedSectionsMutation = useDeletePredefinedSections(
    workspace.id ?? undefined,
  );
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingStructure, setEditingStructure] =
    useState<AppendixStructure | null>(null);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const { data: workspaceDetails } = useWorkspace(
    workspace.id ? workspace.id.toString() : null,
  );
  const createWorkspaceMutation = useCreateWorkspace();
  const updateWorkspaceMutation = useUpdateWorkspace();

  useEffect(() => {
    if (workspaceDetails) {
      const features = workspaceDetails.permissions.map((permission) =>
        permission.feature.id.toString(),
      );

      const certifications = workspaceDetails.workspace_certifications.map(
        (certification) => certification.certification_category,
      );

      setWorkspace((prev) => ({
        ...prev,
        id: workspaceDetails.workspace.id,
        name: workspaceDetails.workspace.name,
        description: workspaceDetails.workspace.description,
        userEvaluationLimit: workspaceDetails.workspace.user_evaluation_limit,
        model_ids: workspaceDetails.accessible_model_ids ?? [],
        userCompanyLimit: workspaceDetails.workspace.user_company_limit,
        userStorageLimit: workspaceDetails.workspace.user_storage_limit,
        userLibraryLimit: workspaceDetails.workspace.user_library_limit,
        namespaces: Array.isArray(workspaceDetails.workspace.namespaces)
          ? workspaceDetails.workspace.namespaces
          : typeof workspaceDetails.workspace.namespaces === "string" &&
              workspaceDetails.workspace.namespaces
            ? JSON.parse(
                workspaceDetails.workspace.namespaces.replace(/'/g, '"'),
              )
            : [],
        workspaceAdminId:
          workspaceDetails.workspace.workspace_admin?.id || null,
        features: features || [],
        certifications: certifications || [],
        existingIconUrl: workspaceDetails.workspace.icon_image
          ? `data:image/png;base64,${workspaceDetails.workspace.icon_image}`
          : null,
        iconImage: null,
        primary_color: workspaceDetails.workspace.primary_color,
        secondary_color: workspaceDetails.workspace.secondary_color,
        accent_color: workspaceDetails.workspace.accent_color,
        terminology: workspaceDetails.workspace.terminology,
        alan_credentials_id: workspaceDetails.alan_credentials?.id ?? null,
        language: workspaceDetails.workspace.language,
        show_locked_features:
          workspaceDetails.workspace.show_locked_features ?? false,
      }));

      if (workspaceDetails.experts) {
        setExperts(workspaceDetails.experts);
      }
      if (workspaceDetails.agent_ids) {
        setComoAgentIds(workspaceDetails.agent_ids);
      }
    }
    setIsChanged(false);
  }, [workspaceDetails]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setWorkspace((prev) => {
      const updatedWorkspace = {
        ...prev,
        [name]:
          name === "userEvaluationLimit" ||
          name === "userCompanyLimit" ||
          name === "userStorageLimit" ||
          name === "userLibraryLimit"
            ? Number(value)
            : value,
      };

      setIsChanged(
        JSON.stringify(updatedWorkspace) !== JSON.stringify(existingWorkspace),
      );

      return updatedWorkspace;
    });
  };

  const handleToggleExpandAll = () => {
    setExpandAll((prev) => !prev);
  };

  const handleFeaturesChange = (value: string, checked: boolean) => {
    setWorkspace((prev) => {
      const updatedFeatures = checked
        ? [...prev.features, value]
        : prev.features.filter((feature) => feature !== value);

      setIsChanged(
        JSON.stringify({ ...prev, features: updatedFeatures }) !==
          JSON.stringify(existingWorkspace),
      );

      return { ...prev, features: updatedFeatures };
    });
  };

  const handleCertificationsChange = (selected: number[]) => {
    setWorkspace((prev) => {
      setIsChanged(
        JSON.stringify({ ...prev, certifications: selected }) !==
          JSON.stringify(existingWorkspace),
      );

      return { ...prev, certifications: selected };
    });
  };

  const handleSubmit = async () => {
    if (!workspace.name || workspace.name.length < 2) {
      enqueueSnackbar(t("workspaceNameTooShort"), { variant: "warning" });
      return;
    }
    if (!workspace.description) {
      enqueueSnackbar(t("descriptionRequired"), { variant: "warning" });
    }

    if (!isChanged) {
      enqueueSnackbar(t("noChangesDetected"), { variant: "info" });
      return;
    }

    const expertsId = experts.map((expert) => expert.id);

    if (workspace.id) {
      updateWorkspaceMutation.mutate(
        {
          workspaceId: workspace.id,
          name: workspace.name,
          description: workspace.description,
          userEvaluationLimit: workspace.userEvaluationLimit,
          userCompanyLimit: workspace.userCompanyLimit,
          userStorageLimit: workspace.userStorageLimit,
          userLibraryLimit: workspace.userLibraryLimit,
          namespaces: workspace.namespaces,
          iconImage: workspace.iconImage,
          workspaceAdminId: workspace.workspaceAdminId || null,
          features: workspace.features.map(Number),
          certifications: workspace.certifications,
          model_ids: workspace.model_ids,
          experts: [],
          primary_color: workspace.primary_color,
          secondary_color: workspace.secondary_color,
          accent_color: workspace.accent_color,
          terminology: workspace.terminology,
          alan_credentials_id: workspace.alan_credentials_id,
          language: workspace.language,
          show_locked_features: workspace.show_locked_features,
          agent_ids: comoAgentIds,
        },
        {
          onSuccess: () => {
            navigate(`/workspace/${workspace.id}`);
            setIsChanged(false);
          },
        },
      );
    } else {
      createWorkspaceMutation.mutate(
        {
          name: workspace.name,
          description: workspace.description,
          userEvaluationLimit: workspace.userEvaluationLimit,
          userCompanyLimit: workspace.userCompanyLimit,
          userStorageLimit: workspace.userStorageLimit,
          userLibraryLimit: workspace.userLibraryLimit,
          namespaces: workspace.namespaces,
          iconImage: workspace.iconImage,
          workspaceAdminId: workspace.workspaceAdminId || null,
          features: workspace.features.map(Number),
          certifications: workspace.certifications,
          model_ids: workspace.model_ids,
          experts: [],
          primary_color: workspace.primary_color,
          secondary_color: workspace.secondary_color,
          accent_color: workspace.accent_color,
          terminology: workspace.terminology,
          alan_credentials_id: workspace.alan_credentials_id,
          language: workspace.language,
          show_locked_features: workspace.show_locked_features,
          agent_ids: comoAgentIds,
        },
        {
          onSuccess: (response) => {
            navigate(`/workspace/${response.workspace_id}`);
            setIsChanged(false);
          },
        },
      );
    }
  };

  const sectionTitleSx = {
    color: workspaceColors.primary_dark,
    fontWeight: 600,
    fontSize: "0.85rem",
    textTransform: "uppercase" as const,
    letterSpacing: 0.5,
    borderBottom: `1px solid ${colors.gray200}`,
    pb: 0.5,
    mb: 2,
  };

  const lightSelectSx = {
    bgcolor: "white",
    fontSize: "0.875rem",
    borderRadius: 2,
    color: `${colors.primary_dark} !important`,
    "& .MuiSelect-select": {
      color: `${colors.primary_dark} !important`,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: `${colors.gray300} !important`,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: `${colors.gray300} !important`,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: `${colors.gray300} !important`,
    },
  };

  const lightMenuProps = {
    PaperProps: {
      sx: {
        bgcolor: `${colors.white} !important`,
        border: `1px solid ${colors.gray200}`,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        "& .MuiMenuItem-root": {
          color: `${colors.primary_dark} !important`,
          fontSize: "0.875rem",
          "&:hover": {
            backgroundColor: `${colors.gray50} !important`,
          },
          "&.Mui-selected": {
            backgroundColor: `${workspaceColors.primary_dark}0F !important`,
            color: `${colors.primary_dark} !important`,
          },
          "&.Mui-selected:hover": {
            backgroundColor: `${workspaceColors.primary_dark}1A !important`,
          },
        },
      },
    },
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: colors.gray50 }}>
      <Box sx={{ maxWidth: "1024px", mx: "auto", px: { xs: 2, sm: 3 }, py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${colors.gray200}`,
            borderLeft: `4px solid ${workspaceColors.primary_dark}`,
            background: colors.white,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <IconButton
              onClick={() => navigate("/workspace")}
              size="small"
              sx={{ color: workspaceColors.primary_dark }}
            >
              <ArrowBackIcon />
            </IconButton>
            <WorkspacesOutlinedIcon
              sx={{ fontSize: 32, color: workspaceColors.primary_dark }}
            />
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, color: workspaceColors.primary_dark }}
            >
              {workspace.id ? t("editWorkspace") : t("createWorkspace")}
            </Typography>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            border: `1px solid ${colors.gray200}`,
            borderLeft: `4px solid ${workspaceColors.primary_dark}`,
            background: colors.white,
          }}
        >
          <Typography sx={sectionTitleSx}>{t("basicInformation")}</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <ComoInput
                label={t("workspaceName")}
                name="name"
                value={workspace.name}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <ComoInput
                label={t("workspaceDescription")}
                name="description"
                value={workspace.description}
                onChange={handleChange}
                multiline
                rows={3}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <UserLimits
                userEvaluationLimit={workspace.userEvaluationLimit}
                userCompanyLimit={workspace.userCompanyLimit}
                userStorageLimit={workspace.userStorageLimit}
                userLibraryLimit={workspace.userLibraryLimit}
                onChange={(limits) => {
                  const updatedWorkspace = { ...workspace, ...limits };
                  setWorkspace(updatedWorkspace);
                  setIsChanged(
                    JSON.stringify(updatedWorkspace) !==
                      JSON.stringify(existingWorkspace),
                  );
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ ...sectionTitleSx, mt: 2, mb: 1.5 }}>
                {t("workspaceIcon")}
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                <PhotoUploadButton
                  onFileChange={(file) => {
                    const updatedWorkspace = { ...workspace, iconImage: file };
                    setWorkspace(updatedWorkspace);
                    setIsChanged(
                      JSON.stringify(updatedWorkspace) !==
                        JSON.stringify(existingWorkspace),
                    );
                  }}
                  existingImage={workspace.existingIconUrl}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ ...sectionTitleSx, mt: 2 }}>
                {t("workspaceAdmin")}
              </Typography>
              <WorkspaceAdminId
                currentAdminEmail={
                  existingWorkspace?.workspace_admin?.email || null
                }
                onChange={(newAdminId) => {
                  const updatedWorkspace = {
                    ...workspace,
                    workspaceAdminId: newAdminId,
                  };
                  setWorkspace(updatedWorkspace);
                  setIsChanged(
                    JSON.stringify(updatedWorkspace) !==
                      JSON.stringify(existingWorkspace),
                  );
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ ...sectionTitleSx, mt: 2 }}>
                {t("namespaces")}
              </Typography>
              <NamespaceField
                namespaces={workspace.namespaces}
                onChange={(updatedNamespaces) => {
                  const updatedWorkspace = {
                    ...workspace,
                    namespaces: updatedNamespaces,
                  };
                  setWorkspace(updatedWorkspace);
                  setIsChanged(
                    JSON.stringify(updatedWorkspace) !==
                      JSON.stringify(existingWorkspace),
                  );
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <ModelAccessSelector
                selectedModelIds={workspace.model_ids}
                onChange={(modelIds) => {
                  setWorkspace((prev) => {
                    const updatedWorkspace = { ...prev, model_ids: modelIds };
                    setIsChanged(
                      JSON.stringify(updatedWorkspace) !==
                        JSON.stringify(existingWorkspace),
                    );
                    return updatedWorkspace;
                  });
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel
                  shrink
                  sx={{
                    color: colors.gray600,
                    fontSize: "0.875rem",
                    "&.Mui-focused": { color: colors.gray600 },
                    "&:hover": {
                      color: colors.gray600,
                    },
                  }}
                >
                  {t("terminology")}
                </InputLabel>
                <Select
                  label={t("terminology")}
                  name="terminology"
                  value={
                    terminologyData?.terminologies?.length
                      ? workspace.terminology
                      : ""
                  }
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    setWorkspace((prev) => {
                      const updatedWorkspace = { ...prev, terminology: value };
                      setIsChanged(
                        JSON.stringify(updatedWorkspace) !==
                          JSON.stringify(existingWorkspace),
                      );
                      return updatedWorkspace;
                    });
                  }}
                  fullWidth
                  sx={lightSelectSx}
                  MenuProps={lightMenuProps}
                >
                  {terminologyData?.terminologies.map((term) => (
                    <MenuItem key={term.value} value={term.value}>
                      {term.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel
                  shrink
                  sx={{
                    color: colors.gray600,
                    fontSize: "0.875rem",
                    "&.Mui-focused": { color: workspaceColors.primary_dark },
                    "&:hover": {
                      color: colors.gray600,
                    },
                  }}
                >
                  {t("defaultLanguage")}
                </InputLabel>
                <Select
                  label={t("defaultLanguage")}
                  name="language"
                  value={workspace.language}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    setWorkspace((prev) => {
                      const updatedWorkspace = { ...prev, language: value };
                      setIsChanged(
                        JSON.stringify(updatedWorkspace) !==
                          JSON.stringify(existingWorkspace),
                      );
                      return updatedWorkspace;
                    });
                  }}
                  fullWidth
                  sx={lightSelectSx}
                  MenuProps={lightMenuProps}
                >
                  <MenuItem value={0}>English</MenuItem>
                  <MenuItem value={1}>German</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderBottom: `1px solid ${colors.gray200}`,
                  pb: 0.5,
                  mb: 2,
                }}
              >
                <Typography
                  sx={{
                    color: workspaceColors.primary_dark,
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  {t("features")}
                </Typography>
                <IconButton
                  size="small"
                  onClick={handleToggleExpandAll}
                  sx={{
                    color: colors.gray500,
                    gap: 0.5,
                    fontSize: "0.75rem",
                    "&:hover": { color: workspaceColors.primary_dark },
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>
                    {expandAll ? t("hideDescriptions") : t("showDescriptions")}
                  </Typography>
                  <ExpandMoreIcon
                    sx={{
                      fontSize: 18,
                      transform: expandAll ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                    }}
                  />
                </IconButton>
              </Box>
              <Grid container spacing={1.5}>
                {Object.entries(Features)
                  .filter(
                    ([key, value]) =>
                      isNaN(Number(key)) &&
                      !USER_ONLY_FEATURES.includes(value as Features),
                  )
                  .map(([_, value]) => {
                    const isSelected = workspace.features.includes(
                      value.toString(),
                    );
                    return (
                      <Grid item key={value} xs={12} sm={6} md={4}>
                        <Card
                          elevation={0}
                          sx={{
                            height: "100%",
                            border: `1px solid ${isSelected ? workspaceColors.accent : colors.gray200}`,
                            borderLeft: `4px solid ${isSelected ? workspaceColors.accent : workspaceColors.primary_dark}`,
                            backgroundColor: isSelected
                              ? alpha(workspaceColors.accent, 0.08)
                              : colors.white,
                            borderRadius: 1.5,
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                              borderColor: workspaceColors.accent,
                              borderLeftColor: workspaceColors.accent,
                            },
                          }}
                        >
                          <CardActionArea
                            sx={{ height: "100%" }}
                            onClick={() =>
                              handleFeaturesChange(
                                value.toString(),
                                !isSelected,
                              )
                            }
                          >
                            <CardContent sx={{ py: 1.25, px: 1.5 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.75,
                                }}
                              >
                                {isSelected && (
                                  <CheckCircleIcon
                                    sx={{
                                      fontSize: 16,
                                      color: workspaceColors.accent,
                                    }}
                                  />
                                )}
                                <Typography
                                  sx={{
                                    fontSize: "0.85rem",
                                    fontWeight: isSelected ? 600 : 500,
                                    color: workspaceColors.primary_dark,
                                  }}
                                >
                                  {
                                    features.find(
                                      (feature) => feature.id === value,
                                    )?.name
                                  }
                                </Typography>
                              </Box>
                              <Collapse
                                in={expandAll}
                                timeout="auto"
                                unmountOnExit
                              >
                                <Typography
                                  sx={{
                                    fontSize: "0.7rem",
                                    color: colors.gray600,
                                    mt: 0.5,
                                  }}
                                >
                                  {
                                    features.find(
                                      (feature) => feature.id === value,
                                    )?.description
                                  }
                                </Typography>
                              </Collapse>
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      </Grid>
                    );
                  })}
              </Grid>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={workspace.show_locked_features}
                    onChange={(e) => {
                      setWorkspace((prev) => {
                        const updatedWorkspace = {
                          ...prev,
                          show_locked_features: e.target.checked,
                        };
                        setIsChanged(
                          JSON.stringify(updatedWorkspace) !==
                            JSON.stringify(existingWorkspace),
                        );
                        return updatedWorkspace;
                      });
                    }}
                    sx={{
                      color: colors.gray400,
                      "&.Mui-checked": { color: workspaceColors.accent },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{ color: colors.primary_dark, fontSize: "0.875rem" }}
                  >
                    {t("showLockedFeatures")}
                  </Typography>
                }
                sx={{ mt: 1 }}
              />
            </Grid>
            <Grid item xs={12}>
              {workspace.features.includes(Features.EVALUATION.toString()) && (
                <CertificationsSelector
                  selectedCertifications={workspace.certifications}
                  onChange={handleCertificationsChange}
                />
              )}
            </Grid>
            {workspace.id && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Typography sx={sectionTitleSx}>
                    {t("predefinedSections")}
                  </Typography>
                  <ComoButton
                    startIcon={<Add />}
                    onClick={() => {
                      setEditingStructure(null);
                      setEditModalOpen(true);
                    }}
                  >
                    {t("createPredefinedSection")}
                  </ComoButton>
                </Box>
                {predefinedSectionsData?.appendix_structures &&
                predefinedSectionsData.appendix_structures.length > 0 ? (
                  <Grid container spacing={2}>
                    {predefinedSectionsData.appendix_structures.map(
                      (structure) => {
                        const sectionCount = structure.structure?.length ?? 0;
                        const subsectionCount =
                          structure.structure?.reduce(
                            (acc, s) => acc + (s.subsections?.length ?? 0),
                            0,
                          ) ?? 0;
                        const title =
                          structure.structure?.[0]?.name ||
                          `Structure #${structure.id}`;

                        return (
                          <Grid item xs={12} sm={6} md={4} key={structure.id}>
                            <Card
                              sx={{
                                height: "100%",
                                "&:hover": {
                                  borderColor: colors.accent,
                                },
                                border: `1px solid ${colors.gray200}`,
                                borderRadius: 2,
                                display: "flex",
                                flexDirection: "column",
                              }}
                            >
                              <CardContent sx={{ flex: 1 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    mb: 1,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      borderRadius: 1,
                                      backgroundColor: colors.accent,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      flexShrink: 0,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        color: colors.white,
                                        fontWeight: 700,
                                        fontSize: "0.85rem",
                                      }}
                                    >
                                      {sectionCount}
                                    </Typography>
                                  </Box>
                                  <Typography
                                    variant="subtitle1"
                                    sx={{
                                      fontWeight: 600,
                                      color: colors.primary_dark,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {title}
                                  </Typography>
                                </Box>
                                <Typography
                                  variant="caption"
                                  sx={{ color: colors.gray500 }}
                                >
                                  {sectionCount}{" "}
                                  {sectionCount === 1
                                    ? t("section")
                                    : t("sections")}
                                  {subsectionCount > 0 && (
                                    <>
                                      {" · "}
                                      {subsectionCount}{" "}
                                      {subsectionCount === 1
                                        ? t("subsection")
                                        : t("subsections")}
                                    </>
                                  )}
                                </Typography>
                              </CardContent>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  gap: 0.5,
                                  px: 1,
                                  pb: 1,
                                }}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setEditingStructure(structure);
                                    setEditModalOpen(true);
                                  }}
                                  sx={{
                                    color: colors.accent,
                                    "&:hover": {
                                      backgroundColor: colors.orange50,
                                    },
                                  }}
                                  title={t("edit")}
                                >
                                  <Edit sx={{ fontSize: 18 }} />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    deletePredefinedSectionsMutation.mutate(
                                      structure.id,
                                    );
                                  }}
                                  sx={{
                                    color: colors.red,
                                    "&:hover": {
                                      backgroundColor: colors.red50,
                                    },
                                  }}
                                  title={t("delete")}
                                >
                                  <Delete sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Box>
                            </Card>
                          </Grid>
                        );
                      },
                    )}
                  </Grid>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ color: colors.gray400, pl: 1 }}
                  >
                    {t("noPredefinedSections")}
                  </Typography>
                )}
              </Grid>
            )}
            {false && (
              <>
                <AlanCredentialSelection
                  selectedCredentialId={workspace.alan_credentials_id}
                  setSelectedCredentialId={(id) => {
                    setWorkspace((prev) => ({
                      ...prev,
                      alan_credentials_id: id,
                    }));
                    setExperts([]);
                    setIsChanged(true);
                  }}
                  setIsChanged={setIsChanged}
                />

                {workspace.alan_credentials_id && (
                  <WorkspaceExpertSelection
                    selectedExperts={experts}
                    setSelectedExperts={setExperts}
                    setIsChanged={setIsChanged}
                    alanCredentialsId={workspace.alan_credentials_id}
                  />
                )}
              </>
            )}

            <Grid item xs={12}>
              {workspace.features.includes(
                Features.COMO_EXPERTS.toString(),
              ) && (
                <ComoAgentSelector
                  selectedAgentIds={comoAgentIds}
                  onChange={(ids) => {
                    setComoAgentIds(ids);
                    setIsChanged(true);
                  }}
                />
              )}
            </Grid>

            <Grid item xs={12}>
              <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
                <ComoButton
                  onClick={handleSubmit}
                  disabled={
                    !isChanged ||
                    createWorkspaceMutation.isPending ||
                    updateWorkspaceMutation.isPending
                  }
                  fullWidth
                  sx={{ py: 1.25, fontWeight: 600 }}
                >
                  {workspace.id
                    ? updateWorkspaceMutation.isPending
                      ? t("saving")
                      : t("saveChanges")
                    : createWorkspaceMutation.isPending
                      ? t("processing")
                      : t("createWorkspace")}
                </ComoButton>
                <IconButton
                  aria-label="Customize Workspace"
                  onClick={handleOpenModal}
                  sx={{
                    bgcolor: workspaceColors.accent,
                    color: workspaceColors.primary_dark,
                    borderRadius: 1.5,
                    px: 2,
                    "&:hover": {
                      bgcolor: workspaceColors.accent,
                      opacity: 0.9,
                    },
                  }}
                >
                  <Box
                    component="img"
                    src="/paint-bucket.png"
                    alt=""
                    sx={{ width: 22, height: 22 }}
                  />
                </IconButton>
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      <CustomizeWorkspaceModal
        open={isModalOpen}
        onClose={handleCloseModal}
        color={{
          primary: workspace.primary_color,
          secondary: workspace.secondary_color,
          accent: workspace.accent_color,
        }}
        setColors={(updatedColors: {
          primary: string;
          secondary: string;
          accent: string;
        }) => {
          setWorkspace((prev) => ({
            ...prev,
            primary_color: updatedColors.primary,
            secondary_color: updatedColors.secondary,
            accent_color: updatedColors.accent,
          }));
          setIsChanged(true);
        }}
      />

      <PredefinedSectionEditModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        structure={editingStructure}
        isSaving={putPredefinedSections.isPending}
        onSave={(sections, structureId) => {
          if (!workspace.id) {
            enqueueSnackbar(t("saveWorkspaceFirst"), { variant: "warning" });
            return;
          }
          putPredefinedSections.mutate(
            {
              workspace_id: workspace.id,
              ...(structureId ? { id: structureId } : {}),
              structure: sections,
            },
            {
              onSuccess: () => {
                setEditModalOpen(false);
                setIsChanged(true);
              },
            },
          );
        }}
      />
    </Box>
  );
};

export default WorkspaceCreator;
